// frontend/src/pages/reportpage/MobileLoanTab.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import globalApi from '../../utils/globalFetchWrapper';
import { API_URL, exportToCSV, formatDateTime } from './reportUtils';

const money = (n) => Number(n || 0).toLocaleString();
const POLL_INTERVAL_MS = 15000; // background refresh while this tab is open

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// globalApi doesn't expose a .patch() method (only .get/.put/.post/.delete are
// used elsewhere on this page), so mutations here use the same raw
// authenticated-fetch pattern ReportsPage.jsx's own exportExcel() already
// uses, rather than guessing at an unconfirmed method.
async function patchJson(endpoint, body) {
  const token = localStorage.getItem('hopeSaccoToken');
  return fetch(`${API_URL}${endpoint}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
}

// ── Eligibility popup ──────────────────────────────────────────────────
// Mirrors MembersListPage.jsx: debounced search, alphabetical-by-name
// default, server-side pagination (defaulting to 50/page here per request).
function EligibilityModal({ onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [bulkWorking, setBulkWorking] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 50,
    hasNext: false,
    hasPrev: false,
  });

  // 300ms debounce, same as MembersListPage.
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(searchDebounce && { search: searchDebounce }),
      });
      const res = await globalApi.get(`/mobile-loans/admin/members/eligibility-list?${params}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load members');

      setMembers(data.data.members || []);
      const total = data.data.total || 0;
      const limit = data.data.limit || 50;
      const currentPage = data.data.page || 1;
      const totalPages = data.data.totalPages || 1;
      setPagination({
        currentPage, totalPages, total, limit,
        hasNext: currentPage < totalPages,
        hasPrev: currentPage > 1,
      });
    } catch (e) {
      setError(e.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, searchDebounce]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever the search term actually changes.
  useEffect(() => {
    setPagination((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }));
  }, [searchDebounce]);

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: Number(newLimit), currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const toggleOne = async (member) => {
    setTogglingId(member.memberId);
    try {
      const res = await patchJson(`/mobile-loans/admin/members/${member.memberId}/credit-status`, {
        creditworthy: !member.creditworthy,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update');
      setMembers((prev) => prev.map((m) => (m.memberId === member.memberId ? { ...m, creditworthy: !member.creditworthy } : m)));
    } catch (e) {
      alert(e.message || 'Failed to update eligibility');
    } finally {
      setTogglingId(null);
    }
  };

  // Scoped to the current page only — with pagination, "visible" means
  // this page's rows, not every match across all pages.
  const markPageEligible = async () => {
    const toMark = members.filter((m) => !m.creditworthy);
    if (toMark.length === 0) return;
    if (!window.confirm(`Mark ${toMark.length} member(s) on this page as ELIGIBLE?`)) return;
    setBulkWorking(true);
    try {
      await Promise.all(
        toMark.map((m) => patchJson(`/mobile-loans/admin/members/${m.memberId}/credit-status`, { creditworthy: true }))
      );
      await load();
    } catch (e) {
      alert('Some updates may have failed — please review the list.');
    } finally {
      setBulkWorking(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, width: 'min(760px, 92vw)', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Manage Member Eligibility</h3>
          <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #eee', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by name or member ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' }}
          />
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>↻ Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={markPageEligible} disabled={bulkWorking || loading || members.length === 0}>
            {bulkWorking ? 'Working...' : '✓ Mark This Page Eligible'}
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0 20px', flex: 1 }}>
          {error ? <div className="error-message" style={{ margin: '12px 0' }}>{error}</div> : null}
          {loading ? (
            <div className="loading" style={{ padding: '20px 0' }}>Loading members...</div>
          ) : members.length === 0 ? (
            <div className="table-note" style={{ padding: '20px 0' }}>No members match your search.</div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Status</th>
                  <th>Toggle</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.memberId}>
                    <td>{m.memberName} ({m.memberId})</td>
                    <td>
                      <span className={m.creditworthy ? 'text-success' : 'text-danger'} style={{ fontWeight: 700 }}>
                        {m.creditworthy ? 'Eligible' : 'Not Eligible'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${m.creditworthy ? 'btn-outline' : 'btn-primary'}`}
                        disabled={togglingId === m.memberId}
                        onClick={() => toggleOne(m)}
                      >
                        {togglingId === m.memberId ? '...' : (m.creditworthy ? 'Mark Not Eligible' : 'Mark Eligible')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#888' }}>
            {pagination.total === 0
              ? 'No members'
              : `Showing ${(pagination.currentPage - 1) * pagination.limit + 1}–${Math.min(pagination.currentPage * pagination.limit, pagination.total)} of ${pagination.total}`}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <button className="btn btn-outline btn-sm" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={!pagination.hasPrev}>
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#555' }}>Page {pagination.currentPage} of {pagination.totalPages}</span>
            <button className="btn btn-outline btn-sm" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={!pagination.hasNext}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const MobileLoanTab = () => {
  // ── Pending approval queue ──────────────────────────────────────────
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState('');
  const [actingId, setActingId] = useState(null);

  // ── Loanees (by month) ──────────────────────────────────────────────
  const [month, setMonth] = useState(currentMonthValue());
  const [monthRequests, setMonthRequests] = useState([]);
  const [monthLoading, setMonthLoading] = useState(true);
  const [monthError, setMonthError] = useState('');
  const [totals, setTotals] = useState(null);

  // ── Eligibility popup ────────────────────────────────────────────────
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);

  const loadPending = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPendingLoading(true);
    if (!silent) setPendingError('');
    try {
      const res = await globalApi.get('/mobile-loans/admin/requests?status=pending');
      if (res.status === 429) { if (!silent) setPendingError('Rate limited - please wait before refreshing'); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load pending requests');
      setPending(data.data.requests || []);
      if (silent) setPendingError('');
    } catch (e) {
      if (e.message === 'Session expired') return;
      if (!silent) setPendingError(e.message || 'Failed to load pending requests');
    } finally {
      if (!silent) setPendingLoading(false);
    }
  }, []);

  const loadMonth = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setMonthLoading(true);
    if (!silent) setMonthError('');
    try {
      const res = await globalApi.get(`/mobile-loans/admin/requests?month=${month}`);
      if (res.status === 429) { if (!silent) setMonthError('Rate limited - please wait before refreshing'); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load mobile loanees');
      setMonthRequests(data.data.requests || []);
      setTotals(data.data.totals || null);
      if (silent) setMonthError('');
    } catch (e) {
      if (e.message === 'Session expired') return;
      if (!silent) setMonthError(e.message || 'Failed to load mobile loanees');
    } finally {
      if (!silent) setMonthLoading(false);
    }
  }, [month]);

  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => { loadMonth(); }, [loadMonth]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadPending({ silent: true });
      loadMonth({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadPending, loadMonth]);

  const loaneeRows = useMemo(
    () => monthRequests.filter((r) => r.status !== 'pending'),
    [monthRequests]
  );

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      const res = await patchJson(`/mobile-loans/admin/requests/${id}/approve`, {});
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to approve');
      await Promise.all([loadPending(), loadMonth()]);
    } catch (e) {
      alert(e.message || 'Failed to approve request');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejecting this request (optional):') || '';
    setActingId(id);
    try {
      const res = await patchJson(`/mobile-loans/admin/requests/${id}/reject`, { reason });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to reject');
      await Promise.all([loadPending(), loadMonth()]);
    } catch (e) {
      alert(e.message || 'Failed to reject request');
    } finally {
      setActingId(null);
    }
  };

  const handleMarkDisbursed = async (id) => {
    const code = window.prompt('M-Pesa transaction code for this disbursement (optional, but recommended for reconciliation):') || '';
    setActingId(id);
    try {
      const res = await patchJson(`/mobile-loans/admin/requests/${id}/mark-disbursed`, { mpesaTransactionCode: code });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to mark as disbursed');
      await loadMonth();
    } catch (e) {
      alert(e.message || 'Failed to mark as disbursed');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="mobile-loan-tab">
      {/* ── Top-level actions ────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setEligibilityModalOpen(true)}>
          👥 Manage Member Eligibility
        </button>
      </div>
      {eligibilityModalOpen && <EligibilityModal onClose={() => setEligibilityModalOpen(false)} />}

      {/* ── Pending Approval ─────────────────────────────────────────── */}
      <div className="report-content">
        <div className="report-title-row">
          <h3>Pending Approval</h3>
          <div className="btn-group">
            <button className="btn btn-outline btn-sm" onClick={() => { loadPending(); loadMonth(); }}>↻ Refresh</button>
          </div>
        </div>

        {pendingError ? <div className="error-message">{pendingError}</div> : null}

        {pendingLoading ? (
          <div className="loading">Loading pending requests...</div>
        ) : pending.length === 0 ? (
          <div className="table-note" style={{ padding: '12px 0' }}>No mobile loan requests awaiting approval.</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Member</th>
                  <th>Amount</th>
                  <th>Interest</th>
                  <th>Repayable</th>
                  <th>M-Pesa Number</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateTime(r.requestedAt)}</td>
                    <td>{r.memberName} ({r.memberId})</td>
                    <td>KES {money(r.amount)}</td>
                    <td>KES {money(r.interest)}</td>
                    <td>KES {money(r.totalRepayable)}</td>
                    <td>{r.mpesaNumber}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={actingId === r.id}
                          onClick={() => handleApprove(r.id)}
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={actingId === r.id}
                          onClick={() => handleReject(r.id)}
                        >
                          ✕ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Mobile Loanees (by month) ────────────────────────────────── */}
      <div className="report-content" style={{ marginTop: 24 }}>
        <div className="report-title-row">
          <h3>Mobile Loanees</h3>
          <div className="btn-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Month:
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc' }}
              />
            </label>
            <button className="btn btn-outline btn-sm" onClick={() => window.print()}>🖨️ Print</button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => exportToCSV(loaneeRows, `mobile-loans-${month}.csv`)}
            >
              ⤓ Export CSV
            </button>
          </div>
        </div>

        {totals ? (
          <div className="compact-summary">
            <div className="compact-summary-item">
              <span className="compact-label">Total Requests This Month:</span>
              <span className="compact-value">{monthRequests.length}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Pending:</span>
              <span className="compact-value">{totals.pendingCount} (KES {money(totals.pendingAmount)})</span>
            </div>
            <div className="compact-summary-item highlight">
              <span className="compact-label">Disbursed:</span>
              <span className="compact-value">{totals.disbursedCount} (KES {money(totals.disbursedAmount)})</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Total Interest:</span>
              <span className="compact-value">KES {money(totals.totalInterest)}</span>
            </div>
          </div>
        ) : null}

        {monthError ? <div className="error-message">{monthError}</div> : null}

        {monthLoading ? (
          <div className="loading">Loading mobile loanees...</div>
        ) : loaneeRows.length === 0 ? (
          <div className="table-note" style={{ padding: '12px 0' }}>
            No members have taken a mobile loan for {month} yet.
            {totals && totals.pendingCount > 0 ? ' (There are pending requests awaiting approval above.)' : ''}
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Member</th>
                  <th>Amount</th>
                  <th>Interest</th>
                  <th>Repayable</th>
                  <th>M-Pesa Number</th>
                  <th>Status</th>
                  <th>Disbursed</th>
                  <th>Txn Code</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loaneeRows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateTime(r.requestedAt)}</td>
                    <td>{r.memberName} ({r.memberId})</td>
                    <td>KES {money(r.amount)}</td>
                    <td>KES {money(r.interest)}</td>
                    <td>KES {money(r.totalRepayable)}</td>
                    <td>{r.mpesaNumber}</td>
                    <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                    <td>{formatDateTime(r.disbursedAt)}</td>
                    <td>{r.mpesaTransactionCode || '-'}</td>
                    <td>
                      {r.status === 'approved' ? (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={actingId === r.id}
                          onClick={() => handleMarkDisbursed(r.id)}
                        >
                          💸 Mark Disbursed
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileLoanTab;
